import { EpisodeOrderType } from '@diritto/common/episode/constants';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { ContentDetailEpisodeList } from 'app-components/contents/detail/ContentDetailEpisodeList';
import { ZIndex } from 'constants/zIndex';
import { useTrackingState } from 'contexts/TrackingProvider';
import { VerticalDivider } from 'icons/VerticalDivider';
import { usePostRelayContentQuery } from 'queries/relay/usePostRelayContentQuery';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getTextLineStyle } from 'styles/common';
import { Tab } from 'ui-components/common/Tab';
import { ContentBodyPaddingContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';

const TRANSITION_TIME = 300;

enum TabValue {
  EpisodeList = 'EpisodeList',
  ContentDetail = 'ContentDetail',
}

const TabItems = [
  { label: '목차', value: TabValue.EpisodeList },
  { label: '작품 설명', value: TabValue.ContentDetail },
];

export const EpisodeInfo = ({
  contentId,
  isAdultContent,
  contentTitle,
  selectedEpisodeId,
  isShow,
  trackerExtraInfo,
  replaceLink = false,
  onClickEpisodeLink,
}: {
  contentId: number;
  isAdultContent: boolean;
  contentTitle: string;
  selectedEpisodeId?: number;
  isShow: boolean;
  replaceLink?: boolean;
  trackerExtraInfo: TrackerExtraInfo;
  onClickEpisodeLink?: () => void;
}) => {
  const { tracker } = useTrackingState();
  const { data } = usePostRelayContentQuery(contentId);
  const [show, setShow] = useState(isShow);
  const [attach, setAttach] = useState(false);
  const [selectedTab, setSelectedTab] = useState(TabValue.EpisodeList);
  const contentDesc = useMemo(() => data?.contentPublic?.description ?? '', [data?.contentPublic?.description]);
  const [selectedEpisodeIdAfterAttach, setSelectedEpisodeIdAfterAttach] = useState(selectedEpisodeId);

  const handleStopPropagation = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation(),
    [],
  );

  useEffect(() => {
    if (selectedTab === TabValue.ContentDetail) {
      tracker.track(TrackingEventType.Click, {
        clickType: ClickTargetType.Tab,
        action: ClickAction.ShowContentDesc,
        trackerExtraInfo,
      });
    }
  }, [selectedTab, tracker, trackerExtraInfo]);

  useEffect(() => {
    if (isShow) {
      setAttach(true);
      setShow(true);
      setTimeout(() => setSelectedEpisodeIdAfterAttach(selectedEpisodeId), TRANSITION_TIME - 100);
    } else {
      setShow(false);
      setSelectedEpisodeIdAfterAttach(undefined);
      setTimeout(() => setAttach(false), TRANSITION_TIME - 100);
    }
  }, [isShow, selectedEpisodeId]);

  return (
    <StyledBackground active={show} isDisplay={attach}>
      <StyledListWrapper>
        <StyledList active={show} onClick={handleStopPropagation}>
          <StyledTopMenu>
            <StyledContentInfo>
              <StyledContentInfoTitle>{contentTitle}</StyledContentInfoTitle>
              <VerticalDivider size={14} />
              <StyledContentInfoEpisodeCount>
                총 {data?.contentPublic?.counts.episodeCount ?? 0}화
              </StyledContentInfoEpisodeCount>
            </StyledContentInfo>
            <Tab items={TabItems} onSelected={setSelectedTab} selectedValue={selectedTab} />
          </StyledTopMenu>

          {selectedTab === TabValue.EpisodeList && (
            <StyledContentDetailEpisodeListWrapper>
              <ContentDetailEpisodeList
                contentId={contentId}
                isAdultContent={isAdultContent}
                selectedEpisodeId={selectedEpisodeIdAfterAttach}
                episodeOrderType={EpisodeOrderType.FirstOrder}
                replaceLink={replaceLink}
                onClick={onClickEpisodeLink}
              />
            </StyledContentDetailEpisodeListWrapper>
          )}

          {selectedTab === TabValue.ContentDetail && (
            <StyledContentDetailEpisodeListWrapper>
              <StyledContentDesc>{contentDesc}</StyledContentDesc>
            </StyledContentDetailEpisodeListWrapper>
          )}
        </StyledList>
      </StyledListWrapper>
    </StyledBackground>
  );
};

const StyledListWrapper = styled(ContentBodyPaddingContainer)`
  position: relative;
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const StyledBackground = styled.div(
  ({ active, isDisplay }: { active: boolean; isDisplay: boolean }) => css`
    position: fixed;
    z-index: ${ZIndex.ViewerEpisodeListBackground};
    display: ${isDisplay ? 'flex' : 'none'};
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.3);
    animation: ${active ? fadeIn : fadeOut} ${TRANSITION_TIME}ms ease-out alternate;
  `,
);

const bottomToUp = keyframes`
  0% {
    bottom: -392px;
  }
  100% {
    bottom: 0;
  }
`;

const upToBottom = keyframes`
  0% {
    bottom: 0;
  }
  100% {
    bottom: -392px;
  }
`;

const StyledList = styled.div(
  ({ active }: { active: boolean }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: ${ZIndex.ViewerEpisodeList};
    animation: ${active ? bottomToUp : upToBottom} ${TRANSITION_TIME}ms ease-out alternate;

    background: ${Color.White};
    width: 100%;

    ${SafeAreaBottomStyles()}

    border-radius: 16px;
    border-bottom-right-radius: initial;
    border-bottom-left-radius: initial;
  `,
);

const StyledTopMenu = styled.div`
  margin: 0 16px;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${Color.Gray200};
  gap: 16px;
`;

const StyledContentInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StyledContentInfoTitle = styled.div`
  ${Typography.Small}
  font-weight: ${FontWeight.Bold};

  ${getTextLineStyle(1)}
`;

const StyledContentInfoEpisodeCount = styled.div`
  ${Typography.XXSmall}
  font-weight: ${FontWeight.Regular};
  flex-shrink: 0;
`;

const StyledContentDetailEpisodeListWrapper = styled.div`
  width: 100%;
  height: 432px;
  overflow-y: scroll;
`;

const StyledContentDesc = styled.div`
  padding: 16px;
  white-space: break-spaces;
  ${Typography.XXSmall}
  font-weight: ${FontWeight.Regular};
  color: ${Color.Gray700};
`;
